// React and hooks
import React, { useState, useRef, useEffect } from "react";

// Third-party libraries
import _ from "lodash";

// Internal hooks
import { useDispatch, useI18n } from "Hooks";

// Selectors and utilities
import { getUserId } from "modules/CommonSelectors";
import { MEETING_SERVICE_OPTIONS } from "../../../modules/Utils";
import { setValue } from "../modules/Utils";
import { TrimTrailingSlashes, isURL } from "Utils";

// Store
import ACLStore from "lib/aclStore";

// Constants
import {
  ENABLE_GOOGLE_DRIVE,
  ENABLE_ONE_DRIVE,
} from "Constants/permissionConstants";

// Icons
import {
  GoogleColored,
  MicrosoftColored,
  ZoomMeetingBrand,
  GoogleMeetBrand,
  MicrosoftTeamsBrand,
  LinkOutlined,
} from "@toddle-edu/ds-icons";

// Components
import ThirdPartyAuth from "AppComponents/ThirdPartyAuth";
import { getSettingValue } from "modules/PermissionModule";
import { updateSettings } from "modules/ConfigurationThunks";
import { useLinkedAccountContext } from "../../../context";

const EMPTY_OBJECT = {};

const useAuthService = () => {
  const authPopUpRef = useRef(null);

  const onAuthClick = () => {
    const authInstance = _.get(authPopUpRef, "current.refs.wrappedInstance");
    if (authInstance?.onAuthClick) {
      authInstance.onAuthClick({ forceAuthorized: true });
    }
  };

  return { authPopUpRef, onAuthClick };
};

const useGoogleAuth = () => {
  const {
    authPopUpRef: googleAuthPopUpRef,
    onAuthClick: onGoogleAuthClick,
  } = useAuthService();
  return { googleAuthPopUpRef, onGoogleAuthClick };
};

const useMicrosoftAuth = () => {
  const {
    authPopUpRef: microsoftAuthPopUpRef,
    onAuthClick: onMicrosoftAuthClick,
  } = useAuthService();
  return { microsoftAuthPopUpRef, onMicrosoftAuthClick };
};

const useZoomAuth = () => {
  const {
    authPopUpRef: zoomAuthPopUpRef,
    onAuthClick: onZoomAuthClick,
  } = useAuthService();
  return { zoomAuthPopUpRef, onZoomAuthClick };
};

const useUserMeetingPreferences = ({
  userId,
  setMeetingService,
  eventModalV2,
  accountOptions,
  createThirdPartyMeetingLinkWrapper,
}) => {
  // don't cache the preference value
  // we want updated value when user change the preference from the meeting service dropdown
  const userMeetingServicePreference = getSettingValue({
    name: "TeacherPortal:schedualMeetingTypePreference",
    userId: userId,
    readFromUserAndOrgLevel: true,
  });

  const dispatch = useDispatch();

  const updateUserMeetingServicePreference = selectedService => {
    if (userMeetingServicePreference !== selectedService) {
      dispatch(
        updateSettings({
          settings: [
            {
              name: "TeacherPortal:schedualMeetingTypePreference",
              value: selectedService,
            },
          ],
          showToastMsg: false,
        })
      );
    }
  };

  const createThirdPartyMeetingLinkWrapperRef = useRef({
    createThirdPartyMeetingLinkWrapper,
    setMeetingService,
  });
  createThirdPartyMeetingLinkWrapperRef.current = {
    createThirdPartyMeetingLinkWrapper,
    setMeetingService,
  };

  useEffect(() => {
    // Set default meeting service based on user preference
    // Only set if no service is currently selected and we have account options
    const currentService = _.get(eventModalV2, "meetingService", null);
    const {
      createThirdPartyMeetingLinkWrapper,
      setMeetingService: setMeetingServiceStable,
    } = createThirdPartyMeetingLinkWrapperRef.current;
    if (
      !currentService &&
      userMeetingServicePreference &&
      !_.isEmpty(accountOptions)
    ) {
      // Check if the preferred service exists in account options
      const preferredOption = _.find(accountOptions, {
        key: userMeetingServicePreference,
      });

      // Edge case 1: If preferred option doesn't exist in the list, don't set anything
      if (!preferredOption) {
        return;
      }

      // Edge case 2: If preferred option is a third-party account that is not connected, don't set it
      if (
        userMeetingServicePreference !== MEETING_SERVICE_OPTIONS.OTHER &&
        !preferredOption.isConnected
      ) {
        return;
      }

      // Set the preferred service as default
      setMeetingServiceStable(userMeetingServicePreference);

      // generate third party meeting link if needed
      if (userMeetingServicePreference !== MEETING_SERVICE_OPTIONS.OTHER) {
        createThirdPartyMeetingLinkWrapper(userMeetingServicePreference);
      }
    }
  }, [accountOptions, userMeetingServicePreference, eventModalV2]);

  return { userMeetingServicePreference, updateUserMeetingServicePreference };
};

const useCustomMeetingLink = ({ updateEventModalV2, t }) => {
  const [linkError, setLinkError] = useState("");

  const setMeetingLink = setValue("meetingLink", updateEventModalV2);

  /**
   * Checks if the provided path is a valid URL.
   *
   * @param {string} path - The input URL or domain string.
   * @returns {boolean} True if valid, false otherwise.
   */
  const isValidUrl = path => {
    const trimmedUrl = _.trim(path);
    if (trimmedUrl === "") {
      return true;
    }

    const meetingURL = TrimTrailingSlashes(trimmedUrl);
    return isURL(meetingURL);
  };

  const debouncedSetMeetingLink = useRef(
    _.debounce(customLink => {
      if (isValidUrl(customLink)) {
        setMeetingLink(customLink);
        setLinkError("");
      } else {
        setLinkError(t("calendar:invalid_meeting_link"));
      }
    }, 300)
  );

  const onChangeCustomMeetingLink = domEvent => {
    const customLink = domEvent?.target?.value;
    if (_.isEmpty(customLink)) {
      debouncedSetMeetingLink.current("");
      return;
    }
    const meetingUrl = _.startsWith(customLink, "https://")
      ? customLink
      : `https://${customLink}`;
    debouncedSetMeetingLink.current(meetingUrl);
  };

  return {
    linkError,
    onChangeCustomMeetingLink,
  };
};

const useMeetingAccountOptions = ({
  linkedThirdPartyAccounts,
  classes,
  t,
  googleAuthPopUpRef,
  microsoftAuthPopUpRef,
  zoomAuthPopUpRef,
  handleThirdPartyAuthSuccess,
  handleThirdPartyAuthFailure,
  connectedAccountsLabel,
  connectedAccountsIcons,
  setIsThirdPartyAuthLoading,
}) => {
  const thirdPartySuccessCallBackHandler = serviceType => {
    return () => {
      handleThirdPartyAuthSuccess(serviceType);
    };
  };

  const getMeetingServiceDropdownOptions = () => {
    const allAccountTypes = [
      {
        type: MEETING_SERVICE_OPTIONS.GOOGLE,
        key: MEETING_SERVICE_OPTIONS.GOOGLE,
        label: (
          <ThirdPartyAuth
            serviceType={MEETING_SERVICE_OPTIONS.GOOGLE}
            ref={googleAuthPopUpRef}
            showChildren={true}
            onSuccessCallBack={thirdPartySuccessCallBackHandler(
              MEETING_SERVICE_OPTIONS.GOOGLE
            )}
            onFailureCallBack={handleThirdPartyAuthFailure}
            setIsThirdPartyAuthLoading={setIsThirdPartyAuthLoading}
          >
            {t("calendar:google_account")}
          </ThirdPartyAuth>
        ),
        icon: <GoogleColored size="xx-small" />,
        labelText: t("calendar:google_account"),
        perm: ENABLE_GOOGLE_DRIVE,
        featurePerm: "Common:AllowGoogleMeetMeetingCreation",
      },
      {
        type: MEETING_SERVICE_OPTIONS.ZOOM,
        key: MEETING_SERVICE_OPTIONS.ZOOM,
        label: (
          <ThirdPartyAuth
            serviceType={MEETING_SERVICE_OPTIONS.ZOOM}
            ref={zoomAuthPopUpRef}
            showChildren={true}
            onSuccessCallBack={thirdPartySuccessCallBackHandler(
              MEETING_SERVICE_OPTIONS.ZOOM
            )}
            onFailureCallBack={handleThirdPartyAuthFailure}
            setIsThirdPartyAuthLoading={setIsThirdPartyAuthLoading}
          >
            {t("calendar:zoom_account")}
          </ThirdPartyAuth>
        ),
        icon: <ZoomMeetingBrand size="xx-small" variant="subtle" />,
        labelText: t("calendar:zoom_account"),
        featurePerm: "Common:AllowZoomMeetingCreation",
      },
      {
        type: MEETING_SERVICE_OPTIONS.MICROSOFT,
        key: MEETING_SERVICE_OPTIONS.MICROSOFT,
        label: (
          <ThirdPartyAuth
            serviceType={MEETING_SERVICE_OPTIONS.MICROSOFT}
            ref={microsoftAuthPopUpRef}
            showChildren={true}
            onSuccessCallBack={thirdPartySuccessCallBackHandler(
              MEETING_SERVICE_OPTIONS.MICROSOFT
            )}
            onFailureCallBack={handleThirdPartyAuthFailure}
            setIsThirdPartyAuthLoading={setIsThirdPartyAuthLoading}
          >
            {t("calendar:microsoft_account")}
          </ThirdPartyAuth>
        ),
        icon: <MicrosoftColored size="xx-small" />,
        labelText: t("calendar:microsoft_account"),
        perm: ENABLE_ONE_DRIVE,
        featurePerm: "Common:AllowMicrosoftTeamsMeetingCreation",
      },
    ];

    const filteredAccountTypesBasedOnPerms = _.filter(
      allAccountTypes,
      accountType => {
        // Check both feature permission and regular permissions
        const isFeatureEnabled =
          !accountType?.featurePerm || ACLStore.can(accountType?.featurePerm);
        const isPermEnabled =
          !accountType.perm || ACLStore.can(accountType.perm);
        return isFeatureEnabled && isPermEnabled;
      }
    );

    const connectedAccounts = [];
    const unconnectedAccounts = [];

    _.map(filteredAccountTypesBasedOnPerms, accountType => {
      const linkedAccount = _.find(linkedThirdPartyAccounts, {
        serviceType: accountType.type,
      });

      if (linkedAccount?.isConnected) {
        connectedAccounts.push({
          ...accountType,
          isConnected: true,
          label: connectedAccountsLabel[accountType.type],
          labelText: connectedAccountsLabel[accountType.type],
          icon: connectedAccountsIcons[accountType.type],
        });
      } else {
        unconnectedAccounts.push({
          ...accountType,
          isConnected: false,
        });
      }
    });

    const options = [
      ...connectedAccounts,
      {
        key: MEETING_SERVICE_OPTIONS.OTHER,
        label: t("calendar:custom_meeting_link"),
        labelText: t("calendar:custom_meeting_link"),
        icon: <LinkOutlined variant="subtle" size="xx-small" />,
        priority: 2,
      },
    ];

    if (!_.isEmpty(unconnectedAccounts)) {
      options.push(
        {
          isDivider: true,
          priority: 1,
        },
        {
          label: (
            <span className="text-label">
              {_.size(unconnectedAccounts) > 1
                ? t("calendar:connect_accounts")
                : t("calendar:connect_account")}
            </span>
          ),
          disabled: true,
          className: classes.sectionHeader,
        },
        ...unconnectedAccounts
      );
    }
    return options;
  };

  const [accountOptions, setAccountOptions] = useState(() =>
    getMeetingServiceDropdownOptions()
  );

  useEffect(() => {
    const options = getMeetingServiceDropdownOptions();
    setAccountOptions(options);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedThirdPartyAccounts]);

  return { accountOptions };
};

const useMeetingLink = ({
  eventModalV2,
  updateEventModalV2,
  createThirdPartyMeetingLink,
  isCreatingThirdPartyMeetingLink,
  classes,
  setToastMsg,
}) => {
  const userId = getUserId();
  const { t } = useI18n(["calendar", "common"]);

  const {
    linkedThirdPartyAccountsList: linkedThirdPartyAccounts,
    refetchLinkedAccounts,
    isLinkedAccountsLoading,
  } = useLinkedAccountContext();

  const selectedService = _.get(eventModalV2, "meetingService", null);

  const setMeetingService = setValue("meetingService", updateEventModalV2);
  const setThirdPartyMeetingLinkData = setValue(
    "thirdPartyMeetingLinkData",
    updateEventModalV2
  );

  const thirdPartyMeetingLinkData = _.get(
    eventModalV2,
    "thirdPartyMeetingLinkData",
    EMPTY_OBJECT
  );

  const { googleAuthPopUpRef, onGoogleAuthClick } = useGoogleAuth();
  const { microsoftAuthPopUpRef, onMicrosoftAuthClick } = useMicrosoftAuth();
  const { zoomAuthPopUpRef, onZoomAuthClick } = useZoomAuth();

  const authClickHandlerMapping = {
    [MEETING_SERVICE_OPTIONS.GOOGLE]: onGoogleAuthClick,
    [MEETING_SERVICE_OPTIONS.MICROSOFT]: onMicrosoftAuthClick,
    [MEETING_SERVICE_OPTIONS.ZOOM]: onZoomAuthClick,
  };

  const connectedAccountsLabel = {
    [MEETING_SERVICE_OPTIONS.GOOGLE]: t("calendar:google_meet"),
    [MEETING_SERVICE_OPTIONS.MICROSOFT]: t("calendar:teams_meeting"),
    [MEETING_SERVICE_OPTIONS.ZOOM]: t("calendar:zoom_meeting"),
  };

  const connectedAccountsIcons = {
    [MEETING_SERVICE_OPTIONS.GOOGLE]: <GoogleMeetBrand size="xx-small" />,
    [MEETING_SERVICE_OPTIONS.MICROSOFT]: (
      <MicrosoftTeamsBrand size="xx-small" />
    ),
    [MEETING_SERVICE_OPTIONS.ZOOM]: <ZoomMeetingBrand size="xx-small" />,
  };

  const createThirdPartyMeetingLinkWrapper = serviceType =>
    createThirdPartyMeetingLink({
      variables: {
        serviceType: serviceType,
      },
    });

  // Use custom meeting link hook
  const { linkError, onChangeCustomMeetingLink } = useCustomMeetingLink({
    updateEventModalV2,
    t,
  });

  const [isThirdPartyAuthLoading, setIsThirdPartyAuthLoading] = useState(false);
  const [isAccountOptionsRefetching, setIsAccountOptionsRefetching] = useState(
    false
  );

  const handleThirdPartyAuthSuccess = async serviceType => {
    setIsAccountOptionsRefetching(true);
    await refetchLinkedAccounts();
    setMeetingService(serviceType);
    createThirdPartyMeetingLinkWrapper(serviceType);
    updateUserMeetingServicePreference(serviceType);
    // small timeout to show loading state in UI
    // to avoid flicker effect in case of fast refetchLinkedAccounts response
    setTimeout(() => {
      setIsAccountOptionsRefetching(false);
    }, 30);
  };

  const handleThirdPartyAuthFailure = () => {
    setIsThirdPartyAuthLoading(false);
    setToastMsg("calendar:third_party_auth_failure");
  };

  // Use meeting account options hook
  const { accountOptions } = useMeetingAccountOptions({
    linkedThirdPartyAccounts,
    classes,
    t,
    googleAuthPopUpRef,
    microsoftAuthPopUpRef,
    zoomAuthPopUpRef,
    handleThirdPartyAuthSuccess,
    handleThirdPartyAuthFailure,
    connectedAccountsLabel,
    connectedAccountsIcons,
    setIsThirdPartyAuthLoading,
  });

  const getSelectedServiceLabel = () => {
    const selectedOption = _.find(accountOptions, {
      key: selectedService,
    });
    return (
      selectedOption?.labelText || t("calendar:select_video_meeting_service")
    );
  };

  // Use meeting preferences hook
  const { updateUserMeetingServicePreference } = useUserMeetingPreferences({
    userId,
    setMeetingService,
    eventModalV2,
    accountOptions,
    createThirdPartyMeetingLinkWrapper,
  });

  /**
   * Handles the selection of a meeting service from the dropdown menu.
   * If the service is already connected, it sets the service, updates user preference, and creates a meeting link.
   * If the service is a custom link option, it sets the service and clears any third-party meeting data.
   * If the service is not connected, it initiates the third-party authentication flow for that service.
   *
   * @param {Object} options - The selected option object
   * @param {string} options.key - The key identifying the selected meeting service
   */
  const handleServiceSelection = ({ key: serviceKey }) => {
    // Ignore clicks on dividers, section headers, or while creating a link
    // isCreatingThirdPartyMeetingLink acts as a lock to prevent multiple simultaneous actions
    //  which may cause inconsistent state (race conditions)
    if (
      serviceKey === "divider" ||
      serviceKey === "section_header" ||
      isCreatingThirdPartyMeetingLink
    )
      return;

    const isCustomLinkSelected = serviceKey === MEETING_SERVICE_OPTIONS.OTHER;

    const { isConnected } = _.find(accountOptions, { key: serviceKey });

    if (isCustomLinkSelected || isConnected) {
      setMeetingService(serviceKey);
      updateUserMeetingServicePreference(serviceKey);

      if (isCustomLinkSelected) {
        setThirdPartyMeetingLinkData({});
      }
      const isSameServiceSelected =
        serviceKey === _.get(eventModalV2, "meetingService", null);

      // Generate a new third-party meeting link if:
      // 1. The selected service is connected (user has authenticated with the provider)
      // 2. Either:
      //    a) The user is switching to a different service (not the same as currently selected),
      //    b) Or, the current service is the same but there is no existing meeting link data
      // (e.g., after clearing or on first selection)
      // This prevents unnecessary API calls and ensures a fresh link is created when needed.
      if (
        isConnected &&
        (!isSameServiceSelected || _.isEmpty(thirdPartyMeetingLinkData))
      ) {
        createThirdPartyMeetingLinkWrapper(serviceKey);
      }
      return;
    }

    // initiate third-party auth flow
    authClickHandlerMapping[serviceKey]?.();
  };

  return {
    selectedService,
    linkError,
    accountOptions,
    isLinkedAccountsLoading,
    handleServiceSelection,
    onChangeCustomMeetingLink,
    getSelectedServiceLabel,
    thirdPartyMeetingLinkData,
    setThirdPartyMeetingLinkData,
    createThirdPartyMeetingLinkWrapper,
    isCreatingThirdPartyMeetingLink,
    userId,
    isThirdPartyAuthLoadingOrRefetching:
      isAccountOptionsRefetching || isThirdPartyAuthLoading,
    updateUserMeetingServicePreference,
  };
};

export default useMeetingLink;
